import React, { useState, useRef, useEffect } from 'react';
import { Icon, Button, Form, Grid, Input, Image } from 'semantic-ui-react';
import { Typography, TextField } from '@mui/material';
import { useCompanyId } from '../../../../core';
import { QATConversationObjectType } from '../../../../ps-types/QATAIConversation';
import { converse } from './QATAIConversation.client';
import { mergeDeepLeft } from 'ramda';
import { ScenarioRunner } from './ScenarioRunner';
import { cloneDeep } from 'lodash';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import { QATAIOtherAnalysisRunner } from './QATAIOtherAnalysisRunner';

// Modern Typing Indicator Component
function TypingIndicator() {
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '4px',
      color: '#6c757d'
    }}>
      <span style={{ fontSize: '14px', marginRight: '8px' }}>Perl Street Assistant is thinking</span>
      <div style={{ display: 'flex', gap: '3px' }}>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              backgroundColor: '#480971',
              animation: `typing 1.4s infinite ease-in-out ${i * 0.2}s`,
            }}
          />
        ))}
      </div>
      <style>
        {`
          @keyframes typing {
            0%, 60%, 100% {
              transform: translateY(0);
              opacity: 0.4;
            }
            30% {
              transform: translateY(-10px);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
}

interface QATConversationMessage {
  role: 'user' | 'assistant' | 'application';
  content: string | React.ReactNode[];
}

export function QATAIConversationChatbot() {
  const [messages, setMessages] = useState<QATConversationMessage[]>([]);
  const [userInput, setUserInput] = useState('');
  const [isLoading] = useState(false);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [currentConversationObject, setCurrentConversationObject] = useState<QATConversationObjectType>({
    next_message: "",
    next_prompt_suggestions: [],
    analysis_runs_history: {
      "pfa-scenario": 0,
      "project-pricing-scenario": 0,
      "property-risk-screening": 0,
      "loi-generation": 0,
    },
    inputs: {}
  });
  const [last3ResponsesFromAgent, setLast3ResponsesFromAgent] = useState<string[]>([]);
  const [conversationInitiatorFormSubmitted, setConversationInitiatorFormSubmitted] = useState(false);

  const companyId = useCompanyId();

  const triggerConversation = async ({input}: {input?: string}) => {
    if (isAgentTyping) {
      console.info("TC: No");
      return;
    }
    setIsAgentTyping(true);
    const aiResponse = await converse(input ?? "", companyId, currentConversationObject, last3ResponsesFromAgent);
    if(aiResponse.user_completed_action){ // Strip the completed action from past run.
      delete aiResponse.user_completed_action;
    }
    setCurrentConversationObject(aiResponse);
    setMessages(prev => [...prev, {role: 'assistant', content: aiResponse.next_message, timestamp: new Date()}]);
    let requestedAction = aiResponse.user_requested_action;
    if(requestedAction){
      if(requestedAction.endsWith('-scenario')){
        let scenarioKind: "PFA" | "Project-Pricing" = requestedAction.replace('-scenario','').toUpperCase() === 'PFA' ? 'PFA' : 'Project-Pricing';
        setMessages(prev => [...prev,
          {role: 'application', 
          content: [<ScenarioRunner key={`${scenarioKind}-${Date.now()}`}
            kind={scenarioKind} 
            inputs={aiResponse.inputs}
            onComplete={()=>{
              handleUserActionRequestCompletion(requestedAction, aiResponse);
            }}
            />],
            timestamp: new Date()}]);
      } else if(requestedAction === 'property-risk-screening' || requestedAction === 'loi-generation') {
        setMessages(prev => [...prev, {role: 'application',
           content: [<QATAIOtherAnalysisRunner key={`${requestedAction}-${Date.now()}`}
            inputs={aiResponse.inputs}
            kind={requestedAction}
            onComplete={()=>{
              handleUserActionRequestCompletion(requestedAction, aiResponse);
            }}
            />], timestamp: new Date()}]);
      }
    }
    setLast3ResponsesFromAgent(messages.filter(r => r.role === 'assistant' && typeof r.content === 'string').map(r => r.content as string).slice(-3));
    setIsAgentTyping(false);
  };

  const handleInitialFormSubmit = async (formData: QATConversationObjectType['inputs']) => {
    const customCurrentConversationObject: QATConversationObjectType = {
      ...currentConversationObject,
      inputs: {
        ...mergeDeepLeft(formData, currentConversationObject.inputs),
      }
    }
    setCurrentConversationObject(customCurrentConversationObject);
    setConversationInitiatorFormSubmitted(true);
  };

  useEffect(()=>{
    if(!conversationInitiatorFormSubmitted) return;
    triggerConversation({});
  }, [currentConversationObject, conversationInitiatorFormSubmitted]);

  const handleUserActionRequestCompletion = async (requestedAction: QATConversationObjectType['user_requested_action'], conversationObject: QATConversationObjectType) => {
    let customCurrentConversationObject: QATConversationObjectType = cloneDeep(conversationObject);
    delete customCurrentConversationObject.user_requested_action;
    if(requestedAction){
      customCurrentConversationObject = {
        ...customCurrentConversationObject,
        "user_completed_action": requestedAction,
        "analysis_runs_history": {
          ...customCurrentConversationObject["analysis_runs_history"],
          [requestedAction]: (customCurrentConversationObject.analysis_runs_history[requestedAction] || 0) + 1} 
        }
    }
    setCurrentConversationObject(customCurrentConversationObject);
  };

  const handleConversationalInput = async (input: string) => {
    // Add user message
    const userMessage: QATConversationMessage = {
      role: 'user',
      content: input,
    };
    setMessages(prev => [...prev, userMessage]);
    triggerConversation({input}).then(()=>{
      setUserInput('');
    })
  };


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (isLoading) {
    return (
      <div style={{
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
        padding: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center' }}>
          <Icon name="spinner" loading size="huge" style={{ color: '#480971', marginBottom: '16px' }} />
          <Typography variant="h6" style={{ color: '#6c757d' }}>
            Loading AI chatbot...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      padding: '24px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Chatbot Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          marginBottom: '24px',
          padding: '20px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
        }}>
          <div style={{ 
            width: '60px', 
            height: '60px', 
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px'
          }}>
            <Image src={"/logos/Perl-Logo-Wide-Black-2x.png"} className="nav logo" />
          </div>
          <div>
            <Typography variant="h4" style={{ 
              margin: 0, 
              fontSize: '24px', 
              fontWeight: '600', 
              color: '#2c3e50'
            }}>
              AI Project Manager
            </Typography>
            <Typography variant="body2" style={{ color: '#6c757d', marginTop: '4px' }}>
              Your intelligent companion for all project management tasks
            </Typography>
          </div>
        </div>

        {/* Chat Messages */}
        <div style={{ 
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          minHeight: '600px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <div style={{ 
            flex: 1, 
            padding: '20px',
            overflowY: 'auto',
            maxHeight: '500px'
          }}>
            {/* Initial Form rendered as agent message */}
              <div style={{ 
                maxWidth: '80%',
                marginBottom: '16px',
              }}>
                <div
                style={{
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  marginBottom: '16px',
                }}
                >
                <Typography variant="body2" style={{ 
                  color: '#2c3e50',
                  lineHeight: '1.5'
                  }}>
                  {`Hello! I am here to assist you with your renewable energy project. Let's start by getting some basic details about your project, once we have them with us, we will deep dive to gather more information on your project, so that we can do further analysis`}
                  </Typography>
                  </div>
              <InitialInputsForm formAlreadySubmitted={conversationInitiatorFormSubmitted} handleSubmit={handleInitialFormSubmit} />
              </div>
            
            {messages.map((message, index) => (
              <div key={index} style={{ 
                marginBottom: '16px',
                display: 'flex',
                justifyContent: message.role !== 'user' ? 'flex-start' : 'flex-end'
              }}>
                <div style={{ 
                  maxWidth: '80%',
                  background: message.role !== 'user' ? '#f8f9fa' : '#480971',
                  color: message.role !== 'user' ? '#2c3e50' : 'white',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <Typography variant="body2" style={{ 
                    color: message.role !== 'user' ? '#2c3e50' : 'white',
                    lineHeight: '1.5'
                  }}>
                    {typeof message.content !== 'string' ? message.content : <ReactMarkdown 
                    children={message.content} 
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw, rehypeHighlight]}
                  />}
                  </Typography>
                </div>
              </div>
            ))}
            
            {/* Typing Indicator */}
            {isAgentTyping && (
              <div style={{ 
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'flex-start'
              }}>
                <div style={{ 
                  maxWidth: '80%',
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  padding: '12px 16px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <TypingIndicator />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

      <div style={{ padding: '16px', borderTop: '1px solid #e0e0e0' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <TextField
            fullWidth
            value={isAgentTyping ? "" : userInput}
            onChange={(e) => setUserInput(e.target.value)}
            placeholder={isAgentTyping ? "" : (!conversationInitiatorFormSubmitted ? "You can start conversation with our agent, once you submit the form" : "You can converse with our agent here.")}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && userInput.trim()) {
                handleConversationalInput(userInput.trim());
              }
            }}
            disabled={isLoading || isAgentTyping || !conversationInitiatorFormSubmitted}
          />
          <Button
            primary
            onClick={() => {
              if (userInput.trim()) {
                handleConversationalInput(userInput.trim());
              }
            }}
            disabled={!userInput.trim() || isLoading || isAgentTyping || !conversationInitiatorFormSubmitted}
            loading={isLoading}
          >
            <Icon name="send" />
          </Button>
        </div>
        
        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {currentConversationObject.next_prompt_suggestions?.map((suggestion, index) => (
            <Button
              key={index}
              size="small"
              onClick={() => handleConversationalInput(suggestion)}
              disabled={isLoading || isAgentTyping}
            >
              {suggestion}
              </Button>
          ))}
        </div>
      </div>
        </div>
      </div>
    </div>
  );
}


function InitialInputsForm({handleSubmit, formAlreadySubmitted}: {handleSubmit: (formData: QATConversationObjectType['inputs'])=>void, formAlreadySubmitted: boolean}) {
  const [formData, setFormData] = useState<Record<string, number | string | null>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Compact form with key fields only
  const keyFields = [
    { key: 'System Size (kW)', type: 'number', placeholder: 'e.g., 1000' },
    { key: 'Cost ($/kW)', type: 'number', placeholder: 'e.g., 1200' },
    { key: 'Lifetime (years)', type: 'number', placeholder: 'e.g., 25' },
    { key: 'Project Annual Opex ($)', type: 'number', placeholder: 'e.g., 50000' },
    { key: 'Project Address', type: 'text', placeholder: 'e.g., 123 Main St, Los Angeles, CA, 90210' }
  ];

  // const ppaFields = [
  //   { key: 'System Size (kW)', type: 'number', placeholder: 'e.g., 1000' },
  //   { key: 'Cost ($/kW)', type: 'number', placeholder: 'e.g., 1200' },
  //   { key: 'Other Revenue ($)', type: 'number', placeholder: 'e.g., 50000' },
  //   { key: 'Upfront Rebates ($)', type: 'number', placeholder: 'e.g., 25000' },
  //   { key: 'Project Address', type: 'text', placeholder: 'e.g., 123 Main St, Los Angeles, CA, 90210' }
  // ];

  const handleFormSubmit = () => {
    // Structure the data according to QATConversationObjectSchema
    const structuredData: QATConversationObjectType['inputs'] = {
      'pfa-scenario': {
        'PV size (kW)': (formData['System Size (kW)'] || null) as number | null,
        'ESS size (kWh)': null,
        'Number of chargers (Units)': null,
        'Total Project Cost': null,
        'Project Annual Revenue': null,
        'Revenue Escalator': null,
        'Project Annual Opex': (formData['Project Annual Opex ($)'] || null) as number | null,
        'Opex Escalator': null,
        'Developer Fee': null,
        'Project Location (State Code)': null,
        'Month and Year PIS/COD (mm/dd/yyyy)': null,
        'Lifetime of Project': (formData['Lifetime (years)'] || null) as number | null,
        'Expected Market Value of Project in 5 years': null,
      },
      'project-pricing-scenario': {
        'Location (State)': null,
        'System Size (kW)': (formData['System Size (kW)'] || null) as number | null,
        'Cost ($/kW)': (formData['Cost ($/kW)'] || null) as number | null,
        'Lifetime (years)': (formData['Lifetime (years)'] || null) as number | null,
        'OpEx ($)': (formData['Project Annual Opex ($)'] || null) as number | null,
        'Other Revenue ($)': null,
        'Upfront Rebates ($)': null,
        'Debt ($)': null,
        'Interest Rate (%)': null,
        'Debt Term (years)': null,
        'Target IRR (%)': null,
      },
      'property-risk-screening': {
        'Address': (formData['Project Address'] || null) as string | null,
      },
      'loi-generation': {
        'Address': (formData['Project Address'] || null) as string | null,
        'Estimated Annual Production (kWh)': null,
        'PV size (kW)': (formData['System Size (kW)'] || null) as number | null,
        'Roof Size (Square Feet)': null,
        'Tenant Occupied Area (Square Feet)': null,
        'StreetAddress': null,
        'County': null,
        'Utility Name': null,
      }
    };
    handleSubmit(structuredData);
    setIsSubmitting(true);
  };

  return (
    <div style={{ 
      maxWidth: '80%',
      background: '#f8f9fa',
      borderRadius: '12px',
      padding: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    }}>
      <Typography variant="subtitle1" style={{ marginBottom: '12px', color: '#2c3e50', fontSize: '14px' }}>
        📋 Quick Overview of Project
      </Typography>
      
      <Form size="small">
        <Grid columns={2} stackable>
          {keyFields.map(field => (
            <Grid.Column key={field.key}>
              <Form.Field>
                <label style={{ fontSize: '12px', marginBottom: '4px' }}>{field.key}</label>
                <Input
                  type={field.type}
                  {...(field.type === 'number' ? {min: 0} : {})}
                  {...(formAlreadySubmitted ? {disabled: true}: {})}
                  placeholder={field.placeholder}
                  value={formData[field.key] || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    [field.key]: field.type === 'number' ? parseFloat(e.target.value) || null : (e.target.value || null)
                  }))}
                  style={{ fontSize: '12px', padding: '6px', width: '100%' }}
                />
              </Form.Field>
            </Grid.Column>
          ))}
        </Grid>
        
        <div style={{ marginTop: '12px', textAlign: 'right' }}>
          <Button
            primary
            size="small"
            onClick={handleFormSubmit}
            disabled={isSubmitting || formAlreadySubmitted}
            style={{ 
              background: 'linear-gradient(135deg, #480971 0%, #6435C9 100%)',
              color: 'white',
              fontSize: '12px'
            }}
          >
            Submit & Start
          </Button>
        </div>
      </Form>
    </div>
  );


}